import { useDrawV2 } from "../DrawV2Context";
import { openAlert } from '../../../../../../_metronic/elements/NotificationAlert';
import { DrawTools } from "../DrawState";
import { useMapLayersV2 } from "../../MapLayersV2Context";
import { useErrorHandler } from "../../../../../../_metronic/helpers/ErrorHandler";
import * as MediaService from "../../../../gis/Fields/services/MediaService"
import * as turf from '@turf/turf';
import CircleAdd from "./icons/circle_add.png";
import LineAdd from "./icons/line_add.png";
import PointMarker from "./icons/marker-point.svg";
import CloudDownload from "./icons/cloud_upload-24px.svg";
import './DrawControl.style.scss';
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

const TOOL_ICONS = {
  [DrawTools.POLYGON]: LineAdd,
  [DrawTools.CIRCLE]: CircleAdd,
  [DrawTools.POINT]: PointMarker,
  [DrawTools.UPLOAD_FILE]: CloudDownload,
}

const DrawToolIcon = ({tool}) => (
  <span className='draw-control-button__icon' aria-hidden='true'>
    <img src={TOOL_ICONS[tool]} alt='' />
  </span>
)

const DrawControl = ({
  className = '',
  label,
  showLabel = true,
  toolLabels,
}) => {
  const draw = useDrawV2();
  const mapLayers = useMapLayersV2();
  const { errorHandler } = useErrorHandler();
  const { t } = useTranslation();
  const defaultToolLabels = useMemo(
    () => ({
      [DrawTools.CIRCLE]: t("common:draw.tools.circle"),
      [DrawTools.POLYGON]: t("common:draw.tools.polygon"),
      [DrawTools.POINT]: t("common:draw.tools.point"),
      [DrawTools.UPLOAD_FILE]: t("common:draw.tools.file"),
    }),
    [t]
  );
  const labels = {...defaultToolLabels, ...(toolLabels || {})};

  if (!draw.isDrawing) {
    return (
      <>
      </>
    )
  }

  const onUpdateFile = async (e) => {
    const file = e.currentTarget.files[0];

    if (file.size > (2 * 1024 * 1024)) {
      openAlert('danger', t("lands:draw.errors.title"), t("lands:draw.errors.file_max_size"));
      return;
    }

    const acceptedTypes = [
      'application/zip',
      'application/vnd.google-earth.kml+xml',
      'application/vnd.google-earth.kmz',
      'application/geo+json',
      'application/json'
    ];

    if (acceptedTypes.indexOf(file.type) === -1 && file.type != "") {
      openAlert('danger', t("lands:draw.errors.title"), t("lands:draw.errors.file_type"));
      return
    }

    mapLayers.setLoading(true);

    const form = new FormData();
    form.append('file', file);

    const res = await errorHandler(
      MediaService.uploadMedia(form)
    );

    if (!res.ok) {
      mapLayers.setLoading(false);
      return;
    }

    if (res.data.data.length < 1) {
      openAlert('danger', t("lands:draw.errors.title"), t("lands:draw.errors.file_not_valid"));
      mapLayers.setLoading(false);
      return;
    }

    const polygons = [];
    const addedPolygons = {};
    res.data.data.forEach(polygon => {
      polygons.push(polygon);
      const n = draw.add(polygon);
      addedPolygons[n] = polygon;
    });

    let joinedPolygon = null;

    for (let i = 0; i < polygons.length; i++) {
      const polygon = polygons[i];
      if (joinedPolygon === null) {
        joinedPolygon = polygon;
      } else {
        joinedPolygon = turf.union(joinedPolygon, polygon);
      }
    }

    mapLayers.fitToPolygon(joinedPolygon);
    mapLayers.setLoading(false);

    Object.keys(addedPolygons).forEach(key => {
      draw.forceCallback({
        features: [
          { id: key, ...addedPolygons[key] }
        ]
      });
    })
  }

  return (
    <>
      <div className={`map-draw-control ${className}`.trim()}>
        <div className="draw-control-shell d-flex flex-row flex-column-fluid">
          {showLabel && (
            <div className="d-flex flex-row-fluid flex-center me-5 draw-control-shell__label">
              <label>{label || t("lands:draw.tools")}</label>
            </div>
          )}
          <div className="d-flex flex-row-fluid draw-control-shell__actions">
            {draw.polygon && (
              <button
                type="button"
                aria-label={labels[DrawTools.POLYGON]}
                className={`btn btn-sm btn-outline btn-outline-dashed btn-outline-dark button-action draw-control-button ${
                  draw.currentMode === DrawTools.POLYGON ? 'draw-control-button--active' : ''
                }`}
                onClick={() => draw.mode(DrawTools.POLYGON)}
              >
                <DrawToolIcon tool={DrawTools.POLYGON} />
                <span className="draw-control-button__label">{labels[DrawTools.POLYGON]}</span>
              </button>
            )}

            {draw.circle && (
              <button
                type="button"
                aria-label={labels[DrawTools.CIRCLE]}
                className={`btn btn-outline btn-sm btn-outline-dashed btn-outline-dark button-action draw-control-button ${
                  draw.currentMode === DrawTools.CIRCLE ? 'draw-control-button--active' : ''
                }`}
                onClick={() => draw.mode(DrawTools.CIRCLE)}
              >
                <DrawToolIcon tool={DrawTools.CIRCLE} />
                <span className="draw-control-button__label">{labels[DrawTools.CIRCLE]}</span>
              </button>
            )}

            {draw.point && (
              <button
                type="button"
                aria-label={labels[DrawTools.POINT]}
                className={`btn btn-sm btn-outline btn-outline-dashed btn-outline-dark button-action draw-control-button ${
                  draw.currentMode === DrawTools.POINT ? 'draw-control-button--active' : ''
                }`}
                onClick={() => draw.mode(DrawTools.POINT)}
              >
                <DrawToolIcon tool={DrawTools.POINT} />
                <span className="draw-control-button__label">{labels[DrawTools.POINT]}</span>
              </button>
            )}

            {draw.file && (
              <button
                type="button"
                aria-label={labels[DrawTools.UPLOAD_FILE]}
                className="btn btn-sm btn-outline btn-outline-dashed btn-outline-dark button-action draw-control-button"
              >
                <input type="file" className="file-selector" onChange={onUpdateFile} />
                <DrawToolIcon tool={DrawTools.UPLOAD_FILE} />
                <span className="draw-control-button__label">{labels[DrawTools.UPLOAD_FILE]}</span>
              </button>
            )}
          </div>
        </div>


      </div>

    </>
  );
};

export default DrawControl;
